import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import {
	listTickets,
	TicketListItem,
	TicketListWithConnection,
	TicketsLoadStatus
} from "client/Api/Workspace/LiveInformation/Autotask/autotaskLiveInformation";
import {
	LiveInformationProvider,
	InterestedParty,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import { AutotaskConnectionIdentifierViewModel } from "client/Api/Apps/EditApp/saveApp";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";

interface AutotaskLiveInformationProviderOptions {
	intervalInSeconds: number;
}

type AutotaskConnectionInterests = {
	items?: true;
};

interface InterestedAutotaskParty extends InterestedParty<number, AutotaskConnectionIdentifierViewModel> {
	updateItems(items: LazyLoadPageResult<TicketListItem>): void;
	updateCreateNewTicketUrl(url: string): void;
	informAboutAutotaskNotBeingConfigured(): void;
	informAboutGlobalRetrievalFailure(): void;
}

class AutotaskLiveInformationProvider extends LiveInformationProvider<
	AutotaskLiveInformationProviderOptions,
	number,
	AutotaskConnectionIdentifierViewModel,
	AutotaskConnectionInterests,
	InterestedAutotaskParty,
	TicketListWithConnection
> {

	constructor(options: AutotaskLiveInformationProviderOptions) {
		super(options);
	}

	protected async getDataFromBackend(
		entries: ReadonlyArray<[AutotaskConnectionIdentifierViewModel, AutotaskConnectionInterests]>
	): Promise<ReadonlyArray<[AutotaskConnectionIdentifierViewModel, TicketListWithConnection]>> {
		const connections: AutotaskConnectionIdentifierViewModel[] = entries.map(x => x[0]);
		const ticketListsByConnection: TicketListWithConnection[] = await listTickets(connections);
		return ticketListsByConnection.map(x => [x.connection, x]);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<AutotaskConnectionInterests, TicketListWithConnection>,
		response: [AutotaskConnectionInterests, TicketListWithConnection]
	) {
		const data: TicketListWithConnection = response[1];

		cacheItem.retrievedInterests.items = true;
		cacheItem.data = data;
	}

	protected informParty(party: InterestedAutotaskParty, cacheItem: [AutotaskConnectionInterests, TicketListWithConnection]) {
		const data: TicketListWithConnection = cacheItem[1];
		switch(data.status) {
			case TicketsLoadStatus.NoError:
				party.updateItems(data.items);
				party.updateCreateNewTicketUrl(data.createNewTicketUrl);
				break;
			case TicketsLoadStatus.NotConfigured:
				party.informAboutAutotaskNotBeingConfigured();
				break;
			case TicketsLoadStatus.UnknownError:
				party.informAboutGlobalRetrievalFailure();
				break;
			default:
				throw new UnreachableCaseError(data.status);
		}
	}

	protected override createResourceMap<TValue>(): Map<AutotaskConnectionIdentifierViewModel, TValue> {
		return new NormalizedMap<AutotaskConnectionIdentifierViewModel, TValue>(
			(key) => `connection_${key.id}`
		);
	}
}

export {
	AutotaskLiveInformationProvider,
	type InterestedAutotaskParty
};
